import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trophy, Users, Calendar, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Database } from "@/integrations/supabase/types";

type Competition = Database['public']['Tables']['sync_competitions']['Row'];

interface SyncCompetitionCardProps {
  competition: Competition;
  participantsCount?: number;
  userScore?: number;
  userRank?: number;
  onJoin: (id: string) => void;
  hasJoined?: boolean;
}

const typeLabels = {
  best_sync: 'Melhor Sincronia',
  most_tracks: 'Mais Músicas',
  longest_streak: 'Maior Sequência',
};

const typeIcons = {
  best_sync: TrendingUp,
  most_tracks: Users,
  longest_streak: Calendar,
};

export const SyncCompetitionCard = ({
  competition,
  participantsCount = 0,
  userScore,
  userRank,
  onJoin,
  hasJoined = false,
}: SyncCompetitionCardProps) => {
  const TypeIcon = typeIcons[competition.competition_type];

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-5 h-5 text-primary" />
            <h3 className="font-semibold truncate">{competition.name}</h3>
          </div>
          {competition.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {competition.description}
            </p>
          )}
        </div>
        <Badge variant="outline" className="shrink-0 ml-2 gap-1">
          <TypeIcon className="w-3 h-3" />
          {typeLabels[competition.competition_type]}
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-3 py-3 border-y">
        <div>
          <p className="text-xs text-muted-foreground">Início</p>
          <p className="text-sm font-medium">
            {format(new Date(competition.start_date), 'dd MMM', { locale: ptBR })}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Término</p>
          <p className="text-sm font-medium">
            {format(new Date(competition.end_date), 'dd MMM', { locale: ptBR })}
          </p>
        </div>
      </div>

      {competition.prize_description && (
        <div className="p-2 bg-primary/10 rounded-lg">
          <p className="text-xs font-medium text-primary">
            🏆 {competition.prize_description}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Participantes</p>
            <p className="font-semibold">{participantsCount}</p>
          </div>
          {hasJoined && userScore !== undefined && (
            <>
              <div>
                <p className="text-muted-foreground">Sua Pontuação</p>
                <p className="font-semibold">{userScore.toFixed(2)}</p>
              </div>
              {userRank && (
                <div>
                  <p className="text-muted-foreground">Posição</p>
                  <p className="font-semibold">#{userRank}</p>
                </div>
              )}
            </>
          )}
        </div>
        {!hasJoined && (
          <Button onClick={() => onJoin(competition.id)} size="sm">
            Participar
          </Button>
        )}
      </div>
    </Card>
  );
};
